import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import AuroraBackground from "../other/AuroraBackground";

// Empty string = same-origin (LB routes /api/* to the backend) so cookies stay first-party.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        console.log("🔍 Processing OAuth callback...");

        // Check if user is authenticated after backend redirect
        const checkAuthResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
          method: "GET",
          credentials: "include", // CRITICAL: Include session cookies
        });

        console.log("Auth check response status:", checkAuthResponse.status);

        if (checkAuthResponse.ok) {
          const userData = await checkAuthResponse.json();
          console.log("✅ User authenticated:", userData);

          // Store user data in localStorage
          localStorage.setItem("user", JSON.stringify(userData.user));

          // Navigate to chat page using React Router
          console.log("🚀 Redirecting to /chat");
          navigate("/chat", { replace: true });
        } else {
          const errorData = await checkAuthResponse.json();
          console.error("❌ Auth check failed:", errorData);
          throw new Error("Authentication failed");
        }
      } catch (error) {
        console.error("❌ Error handling OAuth callback:", error);
        setError("Authentication failed. Redirecting to login...");

        // Redirect to login page after 2 seconds
        setTimeout(() => {
          navigate("/login?error=oauth_failed", { replace: true });
        }, 2000);
      }
    };

    handleOAuthCallback();
  }, [navigate]);

  return (
    <div className="relative flex items-center justify-center min-h-screen text-white overflow-hidden">
      <AuroraBackground />

      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative glass-sheen z-10 w-full max-w-sm mx-6 rounded-4xl glass-strong shadow-e3 p-10 text-center"
      >
        {error ? (
          <>
            <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-rose-500/12 border border-rose-500/30 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-rose-300" />
            </div>
            <p className="text-lg font-semibold text-white">{error}</p>
          </>
        ) : (
          <>
            {/* Accent spinner ring */}
            <div className="mx-auto mb-6 relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-2 border-white/10" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-accent-400 border-r-accent-400 animate-spin shadow-glow-sm" />
            </div>

            <p className="text-lg font-semibold text-white">
              Completing authentication
              <span className="inline-flex ml-0.5">
                <span className="animate-bounce [animation-delay:-0.3s]">.</span>
                <span className="animate-bounce [animation-delay:-0.15s]">.</span>
                <span className="animate-bounce">.</span>
              </span>
            </p>
            <p className="text-sm text-slate-400 mt-2">
              Please wait while we securely redirect you
            </p>

            <div className="mt-6 flex items-center justify-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-[0.18em]">
                <span className="text-white">Cost</span>
                <span className="text-gradient-google">IQ</span>
              </span>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default OAuthCallback;
